import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { getRoleFromToken, hasModuleLevel } from '../../../utils/jwtUtils';
import {
  MdDashboard, MdPeople, MdSecurity, MdDirectionsCar, MdMap,
  MdCheckCircle, MdBusiness, MdDescription, MdBarChart, MdEventAvailable,
  MdCalendarToday, MdMeetingRoom, MdStar, MdEditNote, MdBookmarkBorder,
  MdAccessTime, MdLibraryBooks, MdPublic, MdLocationOn,
  MdMenu, MdClose, MdChevronLeft, MdChevronRight, MdExpandMore,
  MdShield, MdAdminPanelSettings, MdManageAccounts,
  MdWork, MdPersonSearch, MdAssignment, MdSettings,
  MdBugReport, MdBuild, MdHistory,
  MdSupportAgent, MdCategory, MdConfirmationNumber, MdAssessment, MdAddCircleOutline, MdBadge,
  MdInsertChart
} from 'react-icons/md';
import { HiOfficeBuilding } from 'react-icons/hi';
import { FaBox } from 'react-icons/fa';

function Sidebar() {
  const location = useLocation();
  const [isShuttleOpen, setIsShuttleOpen] = useState(false);
  const [isConferenceOpen, setIsConferenceOpen] = useState(false);
  const [isUserRoleOpen, setIsUserRoleOpen] = useState(false);
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [isJobManagementOpen, setIsJobManagementOpen] = useState(false);
  const [isHelpdeskOpen, setIsHelpdeskOpen] = useState(false);
  const [isSystemAdminOpen, setIsSystemAdminOpen] = useState(false);
  const [userRole, setUserRole] = useState('');
  const [userModules, setUserModules] = useState([]);
  const [modulePermissions, setModulePermissions] = useState({});
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  // Helper function to check if user can access a module
  const canAccessModule = (moduleName) => {
    if (userRole === 'SUPER_ADMIN' || userRole === 'ADMIN') return true;
    return userModules.includes(moduleName);
  };

  // Helper to check if user's permission level for a module meets the required level
  // requiredLevel: "READ" | "WRITE" | "DELETE" | "ADMIN"
  const PERM_ORDER = { NONE: 0, BETA: 0, READ: 1, WRITE: 2, DELETE: 3, ADMIN: 4 };
  const canAccessModuleAtLevel = (moduleName, requiredLevel) => {
    if (userRole === 'SUPER_ADMIN' || userRole === 'ADMIN') return true;
    const level = modulePermissions[moduleName] || 'NONE';
    return (PERM_ORDER[level] || 0) >= (PERM_ORDER[requiredLevel] || 0);
  };

  useEffect(() => {
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    if (token) {
      try {
        const claims = getRoleFromToken(token);
        const role = claims?.role?.toUpperCase();
        setUserRole(role);
        setUserModules(claims?.modules || []);
        setModulePermissions(claims?.modulePermissions || {});
      } catch (error) {
        console.error('Error getting role:', error);
      }
    }
  }, []);

  // Auto-expand submenus based on current URL path
  useEffect(() => {
    const pathname = location.pathname;

    // Job Management routes
    if (pathname.startsWith('/admin/jobs/')) {
      setIsJobManagementOpen(true);
    }
    // User & Role Management routes
    else if (pathname.startsWith('/admin/bulk-service') || pathname.startsWith('/admin/roles') || pathname.startsWith('/admin/community/users')) {
      setIsUserRoleOpen(true);
    }
    // Vehicle Management routes
    else if (pathname.startsWith('/admin/vehicles/') || pathname.startsWith('/admin/community/gatemen-approval') || pathname.startsWith('/admin/community/routes') || pathname.startsWith('/admin/community/vehicles')) {
      setIsShuttleOpen(true);
    }
    // Workspace Booking routes
    else if (pathname.startsWith('/admin/community/all-bookings') || pathname.startsWith('/admin/community/amenities') || pathname.startsWith('/admin/community/booking-calendar') || pathname.startsWith('/admin/community/booking-requests') || pathname.startsWith('/admin/community/book-room') || pathname.startsWith('/admin/community/conference-rooms') || pathname.startsWith('/admin/community/my-bookings') || pathname.startsWith('/admin/community/workspace-analytics')) {
      setIsConferenceOpen(true);
    }
    // Organization Management routes
    else if (pathname.startsWith('/admin/admin-locations') || pathname.startsWith('/admin/department-master') || pathname.startsWith('/admin/location-master')) {
      setIsLocationOpen(true);
    }
    // Helpdesk routes
    else if (pathname.startsWith('/admin/helpdesk/')) {
      setIsHelpdeskOpen(true);
    }
    // System Administration routes
    else if (pathname.startsWith('/admin/system/')) {
      setIsSystemAdminOpen(true);
    }
    // Monthly Reports — no submenu, no auto-expand needed
  }, [location.pathname]);

  useEffect(() => {
    // Check if screen is mobile on mount
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setIsCollapsed(false);
        setIsMobileOpen(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setIsCollapsed(prev => !prev);
  };

  const toggleMobileSidebar = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        className="mobile-menu-btn"
        onClick={toggleMobileSidebar}
        aria-label="Toggle menu"
      >
        <span className="hamburger-icon">{isMobileOpen ? <MdClose /> : <MdMenu />}</span>
      </button>

      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="mobile-overlay"
          onClick={toggleMobileSidebar}
        />
      )}

      {/* Desktop Toggle Button - OUTSIDE sidebar to prevent clipping */}
      <button
        type="button"
        className={`sidebar-toggle-btn desktop-only ${isCollapsed ? 'collapsed-toggle' : ''}`}
        onClick={toggleSidebar}
        aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        <span className="toggle-icon">{isCollapsed ? <MdChevronRight /> : <MdChevronLeft />}</span>
      </button>

      <aside className={`admin-sidebar ${isCollapsed ? 'collapsed' : ''} ${isMobileOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="logo-wrapper">
            {isCollapsed ? (
              <div className="logo-icon-container">
                <img src="/corrohealth-logo.png" alt="CorroHealth" className="logo-icon-cropped" />
              </div>
            ) : (
              <img src="/corrohealth-logo.png" alt="CorroHealth" className="logo-full-img" />
            )}
          </div>
        </div>
      <nav>
        <ul className="menu">
          {/* Menus with Submenus - Alphabetical Order */}

          {/* User & Role Management - requires USER_MANAGEMENT or ROLE_MANAGEMENT permission */}
          {(canAccessModule('USER_MANAGEMENT') || canAccessModule('ROLE_MANAGEMENT')) && (
            <li className="menu-group">
              <button
                type="button"
                className="menu-toggle"
                onClick={() => !isCollapsed && setIsUserRoleOpen((prev) => !prev)}
                aria-expanded={isUserRoleOpen}
                title="User & Role Management"
              >
                <span className="menu-icon"><MdPeople /></span>
                {!isCollapsed && <span className="menu-label">User & Role Management</span>}
                {!isCollapsed && <span className={`chevron ${isUserRoleOpen ? 'open' : ''}`}><MdExpandMore /></span>}
              </button>
              {isUserRoleOpen && !isCollapsed && (
                <ul className="submenu">
                  {canAccessModule('USER_MANAGEMENT') && (
                    <li>
                      <NavLink to="/admin/bulk-service" className={({ isActive }) => isActive ? 'active' : undefined}>
                        <span className="submenu-icon"><FaBox /></span>
                        <span className="submenu-label">Bulk Service</span>
                      </NavLink>
                    </li>
                  )}
                  {canAccessModule('ROLE_MANAGEMENT') && (
                    <li>
                      <NavLink to="/admin/roles" className={({ isActive }) => isActive ? 'active' : undefined}>
                        <span className="submenu-icon"><MdAdminPanelSettings /></span>
                        <span className="submenu-label">Role Management</span>
                      </NavLink>
                    </li>
                  )}
                  {canAccessModule('USER_MANAGEMENT') && (
                    <li>
                      <NavLink to="/admin/community/users" className={({ isActive }) => isActive ? 'active' : undefined}>
                        <span className="submenu-icon"><MdManageAccounts /></span>
                        <span className="submenu-label">Users</span>
                    ..  </NavLink>
                    </li>
                  )}
                </ul>
              )}
            </li>
          )}

          {/* Vehicle Management - requires VEHICLE_ENROLLMENT, VEHICLE_ASSIGNMENT, ROUTE_MANAGEMENT, or VEHICLE_ANALYTICS permission */}
          {(canAccessModule('VEHICLE_ENROLLMENT') || canAccessModule('VEHICLE_ASSIGNMENT') || canAccessModule('ROUTE_MANAGEMENT') || canAccessModule('VEHICLE_ANALYTICS')) && (
            <li className="menu-group">
              <button
                type="button"
                className="menu-toggle"
                onClick={() => !isCollapsed && setIsShuttleOpen((prev) => !prev)}
                aria-expanded={isShuttleOpen}
                title="Vehicle Management"
              >
                <span className="menu-icon"><MdDirectionsCar /></span>
                {!isCollapsed && <span className="menu-label">Vehicle Management</span>}
                {!isCollapsed && <span className={`chevron ${isShuttleOpen ? 'open' : ''}`}><MdExpandMore /></span>}
              </button>
              {isShuttleOpen && !isCollapsed && (
                <ul className="submenu">
                  {canAccessModuleAtLevel('VEHICLE_ANALYTICS', 'DELETE') && (
                    <li>
                      <NavLink to="/admin/vehicles/dashboard" className={({ isActive }) => isActive ? 'active' : undefined}>
                        <span className="submenu-icon"><MdBarChart /></span>
                        <span className="submenu-label">Dashboard</span>
                      </NavLink>
                    </li>
                  )}
                  {canAccessModuleAtLevel('VEHICLE_ANALYTICS', 'DELETE') && (
                    <li>
                      <NavLink to="/admin/vehicles/reports" className={({ isActive }) => isActive ? 'active' : undefined}>
                        <span className="submenu-icon"><MdAssessment /></span>
                        <span className="submenu-label">Reports</span>
                      </NavLink>
                    </li>
                  )}
                  {canAccessModule('VEHICLE_ASSIGNMENT') && (
                    <li>
                      <NavLink to="/admin/community/gatemen-approval" className={({ isActive }) => isActive ? 'active' : undefined}>
                        <span className="submenu-icon"><MdCheckCircle /></span>
                        <span className="submenu-label">Gatemen Approval</span>
                      </NavLink>
                    </li>
                  )}
                  {canAccessModule('ROUTE_MANAGEMENT') && (
                    <li>
                      <NavLink to="/admin/community/routes" className={({ isActive }) => isActive ? 'active' : undefined}>
                        <span className="submenu-icon"><MdMap /></span>
                        <span className="submenu-label">Routes</span>
                      </NavLink>
                    </li>
                  )}
                  {canAccessModule('VEHICLE_ENROLLMENT') && (
                    <li>
                      <NavLink to="/admin/community/vehicles" className={({ isActive }) => isActive ? 'active' : undefined}>
                        <span className="submenu-icon"><MdDirectionsCar /></span>
                        <span className="submenu-label">Vehicles</span>
                      </NavLink>
                    </li>
                  )}
                </ul>
              )}
            </li>
          )}

          {/* Workspace Booking - requires CONFERENCE_ROOM or ROOM_BOOKING permission */}
          {(canAccessModule('CONFERENCE_ROOM') || canAccessModule('ROOM_BOOKING') || canAccessModule('AMENITIES')) && (
            <li className="menu-group">
              <button
                type="button"
                className="menu-toggle"
                onClick={() => !isCollapsed && setIsConferenceOpen((prev) => !prev)}
                aria-expanded={isConferenceOpen}
                title="Workspace Booking"
              >
                <span className="menu-icon"><HiOfficeBuilding /></span>
                {!isCollapsed && <span className="menu-label">Workspace Booking</span>}
                {!isCollapsed && <span className={`chevron ${isConferenceOpen ? 'open' : ''}`}><MdExpandMore /></span>}
              </button>
              {isConferenceOpen && !isCollapsed && (
                <ul className="submenu">
                  {canAccessModuleAtLevel('CONFERENCE_ROOM', 'DELETE') && (
                    <li>
                      <NavLink to="/admin/community/all-bookings" className={({ isActive }) => isActive ? 'active' : undefined}>
                        <span className="submenu-icon"><MdLibraryBooks /></span>
                        <span className="submenu-label">All Bookings</span>
                      </NavLink>
                    </li>
                  )}
                  {canAccessModuleAtLevel('CONFERENCE_ROOM', 'DELETE') && (
                    <li>
                      <NavLink to="/admin/community/workspace-analytics" className={({ isActive }) => isActive ? 'active' : undefined}>
                        <span className="submenu-icon"><MdBarChart /></span>
                        <span className="submenu-label">Analytics</span>
                      </NavLink>
                    </li>
                  )}
                  {canAccessModule('AMENITIES') && (
                    <li>
                      <NavLink to="/admin/community/amenities" className={({ isActive }) => isActive ? 'active' : undefined}>
                        <span className="submenu-icon"><MdStar /></span>
                        <span className="submenu-label">Amenities</span>
                      </NavLink>
                    </li>
                  )}
                  {(canAccessModule('CONFERENCE_ROOM') || canAccessModule('ROOM_BOOKING')) && (
                    <li>
                      <NavLink to="/admin/community/booking-calendar" className={({ isActive }) => isActive ? 'active' : undefined}>
                        <span className="submenu-icon"><MdCalendarToday /></span>
                        <span className="submenu-label">Booking Calendar</span>
                      </NavLink>
                    </li>
                  )}
                  {canAccessModuleAtLevel('CONFERENCE_ROOM', 'WRITE') && (
                    <li>
                      <NavLink to="/admin/community/booking-requests" className={({ isActive }) => isActive ? 'active' : undefined}>
                        <span className="submenu-icon"><MdAccessTime /></span>
                        <span className="submenu-label">Booking Requests</span>
                      </NavLink>
                    </li>
                  )}
                  {(canAccessModule('CONFERENCE_ROOM') || canAccessModule('ROOM_BOOKING')) && (
                    <li>
                      <NavLink to="/admin/community/book-room" className={({ isActive }) => isActive ? 'active' : undefined}>
                        <span className="submenu-icon"><MdEditNote /></span>
                        <span className="submenu-label">Book Workspace</span>
                      </NavLink>
                    </li>
                  )}
                  {canAccessModule('CONFERENCE_ROOM') && (
                    <li>
                      <NavLink to="/admin/community/conference-rooms" className={({ isActive }) => isActive ? 'active' : undefined}>
                        <span className="submenu-icon"><MdMeetingRoom /></span>
                        <span className="submenu-label">Workspace</span>
                      </NavLink>
                    </li>
                  )}
                  {(canAccessModule('CONFERENCE_ROOM') || canAccessModule('ROOM_BOOKING')) && (
                    <li>
                      <NavLink to="/admin/community/my-bookings" className={({ isActive }) => isActive ? 'active' : undefined}>
                        <span className="submenu-icon"><MdBookmarkBorder /></span>
                        <span className="submenu-label">My Bookings</span>
                      </NavLink>
                    </li>
                  )}
                </ul>
              )}
            </li>
          )}

          {/* Organization Management Menu - requires LOCATION_MASTER or DEPARTMENT_MANAGEMENT permission */}
          {(canAccessModule('LOCATION_MASTER') || canAccessModule('DEPARTMENT_MANAGEMENT')) && (
            <li className="menu-group">
              <button
                type="button"
                className="menu-toggle"
                onClick={() => !isCollapsed && setIsLocationOpen((prev) => !prev)}
                aria-expanded={isLocationOpen}
                title="Organization Management"
              >
                <span className="menu-icon"><MdBusiness /></span>
                {!isCollapsed && <span className="menu-label">Organization Management</span>}
                {!isCollapsed && <span className={`chevron ${isLocationOpen ? 'open' : ''}`}><MdExpandMore /></span>}
              </button>
              {isLocationOpen && !isCollapsed && (
                <ul className="submenu">
                  {canAccessModule('LOCATION_MASTER') && (
                    <li>
                      <NavLink to="/admin/admin-locations" className={({ isActive }) => isActive ? 'active' : undefined}>
                        <span className="submenu-icon"><MdManageAccounts /></span>
                        <span className="submenu-label">Admin Locations</span>
                      </NavLink>
                    </li>
                  )}
                  {canAccessModule('DEPARTMENT_MANAGEMENT') && (
                    <li>
                      <NavLink to="/admin/department-master" className={({ isActive }) => isActive ? 'active' : undefined}>
                        <span className="submenu-icon"><MdBusiness /></span>
                        <span className="submenu-label">Department Master</span>
                      </NavLink>
                    </li>
                  )}
                  {canAccessModule('LOCATION_MASTER') && (
                    <li>
                      <NavLink to="/admin/location-master" className={({ isActive }) => isActive ? 'active' : undefined}>
                        <span className="submenu-icon"><MdLocationOn /></span>
                        <span className="submenu-label">Location Master</span>
                      </NavLink>
                    </li>
                  )}
                </ul>
              )}
            </li>
          )}

          {/* Job Management Menu - requires job-related permissions */}
          {(canAccessModule('JOB_REQUISITION') || canAccessModule('CANDIDATE_MANAGEMENT') || canAccessModule('JOB_MASTER_DATA') || canAccessModule('JOB_DASHBOARD') || canAccessModule('JOB_REPORTS')) && (
            <li className="menu-group">
              <button
                type="button"
                className="menu-toggle"
                onClick={() => !isCollapsed && setIsJobManagementOpen((prev) => !prev)}
                aria-expanded={isJobManagementOpen}
                title="Job Management"
              >
                <span className="menu-icon"><MdWork /></span>
                {!isCollapsed && <span className="menu-label">Job Management</span>}
                {!isCollapsed && <span className={`chevron ${isJobManagementOpen ? 'open' : ''}`}><MdExpandMore /></span>}
              </button>
              {isJobManagementOpen && !isCollapsed && (
                <ul className="submenu">
                  {canAccessModuleAtLevel('JOB_DASHBOARD', 'DELETE') && (
                    <li>
                      <NavLink to="/admin/jobs/dashboard" className={({ isActive }) => isActive ? 'active' : undefined}>
                        <span className="submenu-icon"><MdBarChart /></span>
                        <span className="submenu-label">Dashboard</span>
                      </NavLink>
                    </li>
                  )}
                  {canAccessModule('JOB_REQUISITION') && (
                    <li>
                      <NavLink to="/admin/jobs/requisitions" className={({ isActive }) => isActive ? 'active' : undefined}>
                        <span className="submenu-icon"><MdAssignment /></span>
                        <span className="submenu-label">Job Requisitions</span>
                      </NavLink>
                    </li>
                  )}
                  {canAccessModule('CANDIDATE_MANAGEMENT') && (
                    <li>
                      <NavLink to="/admin/jobs/candidates" className={({ isActive }) => isActive ? 'active' : undefined}>
                        <span className="submenu-icon"><MdPersonSearch /></span>
                        <span className="submenu-label">Candidate Pipeline</span>
                      </NavLink>
                    </li>
                  )}
                  {canAccessModule('JOB_MASTER_DATA') && (
                    <li>
                      <NavLink to="/admin/jobs/master-data" className={({ isActive }) => isActive ? 'active' : undefined}>
                        <span className="submenu-icon"><MdSettings /></span>
                        <span className="submenu-label">Master Data</span>
                      </NavLink>
                    </li>
                  )}
                  {canAccessModule('JOB_REQUISITION') && (
                    <li>
                      <NavLink to="/admin/jobs/assignment-config" className={({ isActive }) => isActive ? 'active' : undefined}>
                        <span className="submenu-icon"><MdAssignment /></span>
                        <span className="submenu-label">Recruiter Assignment</span>
                      </NavLink>
                    </li>
                  )}
                  {canAccessModule('CANDIDATE_MANAGEMENT') && (
                    <li>
                      <NavLink to="/admin/jobs/coordinators" className={({ isActive }) => isActive ? 'active' : undefined}>
                        <span className="submenu-icon"><MdPeople /></span>
                        <span className="submenu-label">Coordinator Management</span>
                      </NavLink>
                    </li>
                  )}
                </ul>
              )}
            </li>
          )}

          {/* Helpdesk Management - requires HELPDESK or HELPDESK_ADMIN permission */}
          {(canAccessModule('HELPDESK') || canAccessModule('HELPDESK_ADMIN')) && (
            <li className="menu-group">
              <button
                type="button"
                className="menu-toggle"
                onClick={() => !isCollapsed && setIsHelpdeskOpen((prev) => !prev)}
                aria-expanded={isHelpdeskOpen}
                title="Helpdesk"
              >
                <span className="menu-icon"><MdSupportAgent /></span>
                {!isCollapsed && <span className="menu-label">Helpdesk</span>}
                {!isCollapsed && <span className={`chevron ${isHelpdeskOpen ? 'open' : ''}`}><MdExpandMore /></span>}
              </button>
              {isHelpdeskOpen && !isCollapsed && (
                <ul className="submenu">
                  <li>
                    <NavLink to="/admin/helpdesk/create-ticket" className={({ isActive }) => isActive ? 'active' : undefined}>
                      <span className="submenu-icon"><MdAddCircleOutline /></span>
                      <span className="submenu-label">Create Ticket</span>
                    </NavLink>
                  </li>
                  {canAccessModuleAtLevel('HELPDESK_ADMIN', 'DELETE') && (
                  <li>
                    <NavLink to="/admin/helpdesk/analytics" className={({ isActive }) => isActive ? 'active' : undefined}>
                      <span className="submenu-icon"><MdBarChart /></span>
                      <span className="submenu-label">Analytics</span>
                    </NavLink>
                  </li>
                  )}
                  <li>
                    <NavLink to="/admin/helpdesk/dashboard" className={({ isActive }) => isActive ? 'active' : undefined}>
                      <span className="submenu-icon"><MdConfirmationNumber /></span>
                      <span className="submenu-label">Ticket Dashboard</span>
                    </NavLink>
                  </li>
                  {canAccessModuleAtLevel('HELPDESK_ADMIN', 'ADMIN') && (
                  <li>
                    <NavLink to="/admin/helpdesk/master-data" className={({ isActive }) => isActive ? 'active' : undefined}>
                      <span className="submenu-icon"><MdCategory /></span>
                      <span className="submenu-label">Master Data</span>
                    </NavLink>
                  </li>
                  )}
                  {canAccessModuleAtLevel('HELPDESK_ADMIN', 'DELETE') && (
                  <li>
                    <NavLink to="/admin/helpdesk/id-card-history" className={({ isActive }) => isActive ? 'active' : undefined}>
                      <span className="submenu-icon"><MdBadge /></span>
                      <span className="submenu-label">ID Card History</span>
                    </NavLink>
                  </li>
                  )}
                  {canAccessModuleAtLevel('HELPDESK_ADMIN', 'DELETE') && (
                  <li>
                    <NavLink to="/admin/helpdesk/reports" className={({ isActive }) => isActive ? 'active' : undefined}>
                      <span className="submenu-icon"><MdAssessment /></span>
                      <span className="submenu-label">Reports</span>
                    </NavLink>
                  </li>
                  )}
                </ul>
              )}
            </li>
          )}

          {/* Single Menu Items - Alphabetical Order */}
          {userRole === 'SUPER_ADMIN' && (
            <li>
              <NavLink to="/admin/monthly-reports" className={({ isActive }) => isActive ? 'active' : undefined} title="Monthly Reports">
                <span className="menu-icon"><MdInsertChart /></span>
                {!isCollapsed && <span className="menu-label">Monthly Reports</span>}
              </NavLink>
            </li>
          )}

          {canAccessModule('REPORTS') && (
            <li>
              <NavLink to="/admin/reports" className={({ isActive }) => isActive ? 'active' : undefined} title="Reports">
                <span className="menu-icon"><MdDescription /></span>
                {!isCollapsed && <span className="menu-label">Reports</span>}
              </NavLink>
            </li>
          )}

          {/* System Administration - SuperAdmin Only */}
          {userRole === 'SUPER_ADMIN' && (
            <li className="menu-group">
              <button
                type="button"
                className="menu-toggle"
                onClick={() => !isCollapsed && setIsSystemAdminOpen((prev) => !prev)}
                aria-expanded={isSystemAdminOpen}
                title="System Administration"
              >
                <span className="menu-icon"><MdBuild /></span>
                {!isCollapsed && <span className="menu-label">System Administration</span>}
                {!isCollapsed && <span className={`chevron ${isSystemAdminOpen ? 'open' : ''}`}><MdExpandMore /></span>}
              </button>
              {isSystemAdminOpen && !isCollapsed && (
                <ul className="submenu">
                  <li>
                    <NavLink to="/admin/system/error-logs" className={({ isActive }) => isActive ? 'active' : undefined}>
                      <span className="submenu-icon"><MdBugReport /></span>
                      <span className="submenu-label">Error Logs</span>
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/admin/system/audit-logs" className={({ isActive }) => isActive ? 'active' : undefined}>
                      <span className="submenu-icon"><MdHistory /></span>
                      <span className="submenu-label">Audit Logs</span>
                    </NavLink>
                  </li>
                </ul>
              )}
            </li>
          )}
        </ul>
      </nav>
    </aside>
    </>
  );
}

export default Sidebar;

